import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { Column, Row } from '../../core/Components'
import { FavoriteArpMediaButton } from '../../core/FavoriteButton'
import ProfileAvatar from '../../platform/explore/common/ProfileAvatar'
import { PlayerSection } from './Common'

const StyledPlayerSection = styled(PlayerSection, {
  fontFamily: 'DM Sans',
  variants: {
    isMobile: {
      true: {
        maxWidth: 'calc(100% - (32px + 16px + 16px + 32px))'
      }
    }
  }
})

const Title = styled('span', {
  maxWidth: '80%',
  font: 'dm',
  overflow: 'hidden',
  fontSize: '14px',
  lineHeight: '14px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  paddingBottom: '5px',
  color: '#000000',
  fontWeight: '$medium',
  '@tablet': {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
})

const Subtitle = styled('div', {
  font: 'dm',
  overflow: 'visible',
  fontSize: '12px',
  lineHeight: '12px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  color: '#14141499',
  fontWeight: '$medium',
  '@tablet': {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
})

const TextContainer = styled(Column, {
  marginLeft: '12px',
  width: '100%',
  height: '100%',
  justifySelf: 'center'
})

const Container = styled(Row, {
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  '@tablet': {
    maxWidth: '330px'
  }
})

const segmentDetails = (type, id) => {
  window.analytics.track('Button Clicked', {
    button: 'see details',
    source: window.location.pathname,
    location: 'global player',
    type: type,
    id: id
  })
}

const segmentArtist = (artistName, artistEthAddress) => {
  window.analytics.track('Button Clicked', {
    button: 'see artist',
    source: window.location.pathname,
    location: 'global player',
    artistName: artistName,
    artistEthAddress: artistEthAddress
  })
}

const useMediaTitleSection = (mediaOverride: IArpMedia | undefined) => {
  const { globalPlayer } = useTreeState()
  const [arp, setArp] = useState<IArpMedia | undefined>(mediaOverride)

  useEffect(() => {
    setArp(mediaOverride)
  }, [mediaOverride])

  useEffect(() => {
    if (globalPlayer.arpMediaIndex === null || mediaOverride) return
    ArpRegistry.getByIdAsync(globalPlayer.arpMediaIndex).then((arpMedia) => setArp(arpMedia))
  }, [globalPlayer.arpMediaIndex])

  if (arp === undefined) return {}
  return {
    media: arp,
    artistEth: arp.user.walletAddress,
    title: arp.metadata.title,
    artistName: arp.user.displayName
  }
}

interface Props {
  hideAvatar?: boolean
  isMobile?: boolean
  mediaOverride?: IArpMedia
}

const MediaTitleSection: FC<Props> = ({ hideAvatar, isMobile, mediaOverride }) => {
  const { artistEth, title, artistName, media } = useMediaTitleSection(mediaOverride)
  const navigate = useNavigate()

  if (!media) return <PlayerSection />

  const navigateToDetailPage = () => {
    if (isMobile) return
    segmentDetails(media.type, media.id)
    navigate(`/${media.type}?token=${media.id}`)
  }
  const navigateToArtistPage = () => {
    if (isMobile) return
    segmentArtist(artistName, artistEth)
    navigate(`/profile/${artistEth}`)
  }
  return (
    <StyledPlayerSection isMobile={isMobile}>
      <Container>
        <Row css={{ width: '100%' }} verticalCenter>
          {!hideAvatar && <ProfileAvatar eth={artistEth} large square />}
          <TextContainer>
            <Title onClick={navigateToDetailPage}>{title}</Title>
            <Subtitle onClick={navigateToArtistPage}>{artistName}</Subtitle>
          </TextContainer>
        </Row>
        <Row verticalCenter justifyContentEnd>
          <FavoriteArpMediaButton
            media={media}
            css={{ color: '#F6658E' }}
            location="global player"
          />
          <Title css={{ padding: '0px' }}>{media?.favoriteCount || 0}</Title>
        </Row>
      </Container>
    </StyledPlayerSection>
  )
}

export default observer(MediaTitleSection)
